import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/bots")({ component: AdminBots });

type Tier = {
  id: string; name: string; slug: string; description: string | null;
  daily_roi: number; min_amount: number; max_amount: number | null;
  duration_days: number; risk_level: string; is_active: boolean; sort_order: number;
};

function AdminBots() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Tier | null>(null);

  const { data: tiers = [] } = useQuery({
    queryKey: ["admin-bot-tiers"],
    queryFn: async () => {
      const { data } = await supabase.from("bot_tiers").select("*").order("sort_order");
      return (data ?? []) as Tier[];
    },
  });

  const { data: logs = [] } = useQuery({
    queryKey: ["admin-bot-tier-logs"],
    queryFn: async () => {
      const { data } = await supabase.from("admin_activity_log").select("*")
        .in("action", ["bot_tier_update", "bot_tier_toggle", "bot_tier_payout_config"])
        .order("created_at", { ascending: false }).limit(50);
      return data ?? [];
    },
  });

  const logChange = async (action: string, target_id: string, meta: Record<string, unknown>) => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    await supabase.from("admin_activity_log").insert({ admin_id: u.user.id, action, target_id, meta: meta as never });
  };

  const toggle = useMutation({
    mutationFn: async ({ tier, active }: { tier: Tier; active: boolean }) => {
      const { error } = await supabase.from("bot_tiers").update({ is_active: active }).eq("id", tier.id);
      if (error) throw error;
      await logChange("bot_tier_toggle", tier.id, { name: tier.name, from: tier.is_active, to: active });
    },
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["admin-bot-tiers"] });
      qc.invalidateQueries({ queryKey: ["admin-bot-tier-logs"] });
      qc.invalidateQueries({ queryKey: ["bot-tiers"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: async (next: Tier) => {
      const prev = tiers.find((t) => t.id === next.id)!;
      const patch = {
        name: next.name, description: next.description,
        daily_roi: next.daily_roi, min_amount: next.min_amount,
        max_amount: next.max_amount, duration_days: next.duration_days,
        risk_level: next.risk_level, sort_order: next.sort_order,
      };
      const { error } = await supabase.from("bot_tiers").update(patch).eq("id", next.id);
      if (error) throw error;
      const changed: Record<string, { from: unknown; to: unknown }> = {};
      (Object.keys(patch) as (keyof typeof patch)[]).forEach((k) => {
        if (String(prev[k] ?? "") !== String(patch[k] ?? "")) {
          changed[k] = { from: prev[k], to: patch[k] };
        }
      });
      const payoutChanged = "daily_roi" in changed || "duration_days" in changed;
      await logChange(payoutChanged ? "bot_tier_payout_config" : "bot_tier_update",
        next.id, { name: next.name, changes: changed });
    },
    onSuccess: () => {
      toast.success("Bot tier saved");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin-bot-tiers"] });
      qc.invalidateQueries({ queryKey: ["admin-bot-tier-logs"] });
      qc.invalidateQueries({ queryKey: ["bot-tiers"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold">Bot tiers</h2>
          <p className="text-sm text-muted-foreground">Manage Basic / Pro / Elite bot tiers, activation, and payout configuration.</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Min</TableHead>
              <TableHead>Max</TableHead>
              <TableHead>Daily ROI</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Active</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tiers.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell className="capitalize">{t.risk_level}</TableCell>
                <TableCell className="font-mono">${Number(t.min_amount).toLocaleString()}</TableCell>
                <TableCell className="font-mono">{t.max_amount ? `$${Number(t.max_amount).toLocaleString()}` : "—"}</TableCell>
                <TableCell className="font-mono">{Number(t.daily_roi)}%</TableCell>
                <TableCell className="font-mono">{t.duration_days}d</TableCell>
                <TableCell>
                  <Switch checked={t.is_active} onCheckedChange={(v) => toggle.mutate({ tier: t, active: v })} />
                </TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => setEditing(t)}>Edit</Button></TableCell>
              </TableRow>
            ))}
            {tiers.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No bot tiers</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold">Audit log</h2>
          <p className="text-sm text-muted-foreground">Recent bot tier changes.</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="font-mono text-xs">{new Date(l.created_at).toLocaleString()}</TableCell>
                <TableCell className="text-xs">{l.action}</TableCell>
                <TableCell className="text-xs font-mono break-all max-w-md">{JSON.stringify(l.meta)}</TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No changes yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit bot tier</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="col-span-2"><Label>Description</Label>
                <Input value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div><Label>Daily ROI (%)</Label>
                <Input type="number" step="0.001" value={editing.daily_roi}
                  onChange={(e) => setEditing({ ...editing, daily_roi: Number(e.target.value) })} />
              </div>
              <div><Label>Duration (days)</Label>
                <Input type="number" value={editing.duration_days}
                  onChange={(e) => setEditing({ ...editing, duration_days: Number(e.target.value) })} />
              </div>
              <div><Label>Min amount</Label>
                <Input type="number" value={editing.min_amount}
                  onChange={(e) => setEditing({ ...editing, min_amount: Number(e.target.value) })} />
              </div>
              <div><Label>Max amount (blank = none)</Label>
                <Input type="number" value={editing.max_amount ?? ""}
                  onChange={(e) => setEditing({ ...editing, max_amount: e.target.value === "" ? null : Number(e.target.value) })} />
              </div>
              <div><Label>Risk level</Label>
                <Input value={editing.risk_level} onChange={(e) => setEditing({ ...editing, risk_level: e.target.value })} />
              </div>
              <div><Label>Sort order</Label>
                <Input type="number" value={editing.sort_order}
                  onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editing && save.mutate(editing)} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}